// components/AnimateHeader.tsx
/**
 * MORPHING HEADER COMPONENT - Animation Control Guide
 * ====================================================
 *
 * This header morphs between two states based on scroll position:
 * - Initial: Transparent background, badges on far edges, narrow nav pill
 * - Scrolled: Dark background, badges merge into expanded nav pill
 *
 * KEY ANIMATION VARIABLES TO CUSTOMIZE:
 *
 * 1. SCROLL TRIGGER DISTANCE
 *    - mergeDistance: pixels to scroll for full animation (default: 260)
 *
 * 2. HEADER BACKGROUND
 *    - Uses viewport units for responsive sizing
 *    - headerHeight: 7.5vh to 6.75vh animation
 *    - Background opacity controlled separately
 *
 * 3. PILL WIDTH ANIMATION (Lines 264-279)
 *    - Morphs from badge-to-badge width to centered
 *    - Initial: Distance between badges (initialWidthVW)
 *    - Final: 480px/360px centered to accommodate badges
 *    - Fixed nav container (320px) prevents shifting
 *    - TO ADJUST: Modify lines 273-279 for custom widths
 *
 * 4. BADGE ANIMATIONS (Lines 222-253)
 *    - Positions use viewport width (vw) units
 *    - leftXVW/rightXVW: animate badge positions
 *    - Responsive sizing based on viewport
 *    - TO ADJUST BADGE POSITIONS:
 *      - Lines 227: edgePadVW (initial distance from edges)
 *      - Line 231: finalGapVW (final gap between badges)
 *
 * 5. ANIMATION SPEED
 *    - All animations use scroll-based progress (0 to 1)
 *    - Adjust array breakpoints to control animation timing
 *    - Example: [0, 0.5, 1] = start, midpoint, end
 *
 * 6. ELEMENT SIZES & RESPONSIVENESS
 *    - External badges: h-10 w-10 (40px base)
 *    - Pill height: h-14 (56px)
 *    - All positions use viewport units for true responsiveness
 *    - Fixed nav container width prevents layout shifts
 */
"use client";

import * as React from "react";
import { motion, useScroll, useTransform, useSpring, MotionValue, AnimatePresence } from "framer-motion";
import { User, Settings, LogOut, FlaskConical } from "lucide-react";

type NavItem = { label: "Overview" | "Open Positions" | "Profile" | (string & {}); href?: string };

// Profile menu types and interfaces
interface Profile {
  email: string;
  name: string;
  phone?: string;
  cvFileName?: string;
  joinedAt: string;
  title?: string;
  skills?: any;
  experience?: any;
  education?: any;
  location?: string;
  about?: string;
}

interface User {
  email: string;
  name: string;
}

export type AnimateHeaderProps = {
  /** Scroll distance in px until the header is fully "merged". */
  mergeDistance?: number;
  /** Left logo (brand) image src (default given below). */
  logoSrcLeft?: string;
  /** Right profile/avatar image src (optional). */
  profileSrcRight?: string;
  /** Tabs to render inside the pill. Defaults to Overview/Open Positions/Profile. */
  navItems?: NavItem[];
  /** Controlled active tab label; if omitted, component manages its own. */
  activeTab?: string;
  /** Callback when tab changes (fires before any href navigation). */
  onTabChange?: (label: string) => void;
  /** Extra classes for the sticky wrapper (z-index/layout tweaks). */
  className?: string;

  // Profile menu props
  /** User profile data */
  profile?: Profile | null;
  /** User auth data */
  user?: User | null;
  /** Loading state */
  loading?: boolean;
  /** Tab setter function */
  setActiveTab?: (tab: string) => void;
  /** Logout function */
  logout?: () => void;
  /** Development mode flag */
  isDevelopmentMode?: boolean;
  /** Reset applications function (dev only) */
  handleResetAllApplications?: () => void;
};

/* ---------- Small Utilities ---------- */

function useViewportWidth(defaultWidth = 1200) {
  const [vw, setVw] = React.useState<number>(
    typeof window === "undefined" ? defaultWidth : window.innerWidth
  );
  React.useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return vw;
}

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

const DEFAULT_NAV: NavItem[] = [
  { label: "Overview", href: "#" },
  { label: "Open Positions", href: "#" },
  { label: "Profile", href: "#" },
];

/* ---------- Visual Primitives ---------- */

function Circle({
  className,
  children,
  title,
}: {
  className?: string;
  children?: React.ReactNode;
  title?: string;
}) {
  return (
    <div
      title={title}
      className={cx(
        "inline-flex items-center justify-center h-10 w-10 rounded-full",
        "bg-gradient-to-br from-amber-400/90 to-orange-500/90 backdrop-blur-xl shadow-lg ring-1 ring-white/20",
        className
      )}
    >
      {children}
    </div>
  );
}


function Pill({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cx(
        "flex items-center gap-3 px-4 h-14 rounded-full",
        "bg-gray-900/80 backdrop-blur-2xl",
        "ring-1 ring-white/10 shadow-2xl",
        className
      )}
    >
      {children}
    </div>
  );
}

/* ---------- Main Component ---------- */

export default function AnimateHeader({
  mergeDistance = 400,
  logoSrcLeft = "/assets/images/branding/Website-Icon.png",
  navItems = DEFAULT_NAV,
  activeTab,
  onTabChange,
  className,
  // Profile menu props
  profile,
  user,
  loading,
  setActiveTab,
  logout,
  isDevelopmentMode,
  handleResetAllApplications,
}: AnimateHeaderProps) {
  // ============================================================================
  // ANIMATION CONTROL VARIABLES - Customize animations here
  // ============================================================================

  // Main progress: 0 at top → 1 after mergeDistance px scrolled
  const { scrollY } = useScroll();
  const rawProgress = useTransform(scrollY, [0, mergeDistance], [0, 1], { clamp: true });

  // Add spring animation for car braking effect (momentum/delay)
  const progress = useSpring(rawProgress, {
    stiffness: 120,   // How quickly it responds (lower = more delay)
    damping: 40,      // How much it bounces (higher = less bounce)
    mass: 1          // Weight of the animation (higher = more inertia)
  });

  // HEADER ANIMATIONS
  // Height animation: restored original pixel values for consistent padding
  const headerHeightEnd = '108px'; // Final header height after scroll
  const headerHeight = useTransform(progress, [0, 1], [120, 108]);
  // Background opacity: keep transparent throughout
  const headerBgOpacity = useTransform(progress, [0, 1], [0, 0]);
  // Border opacity: keep transparent throughout
  const headerBorderOpacity = useTransform(progress, [0, 1], [0, 0]);

  // External badges animation using viewport units
  const vw = useViewportWidth();
  const isMobile = vw < 768;

  // Badge size - Fixed pixels for ALL resolutions (both mobile and desktop)
  const desktopBadgeSizePx = 40; // Desktop: 40px badges (adjustable)
  const mobileBadgeSizePx = 24;  // Mobile: 24px badges (adjustable)

  const badgeSizeVW = isMobile
    ? (mobileBadgeSizePx / vw) * 100         // Mobile: fixed 24px
    : (desktopBadgeSizePx / vw) * 100;       // Desktop: fixed 40px

  // Edge padding - Fixed pixels for ALL resolutions (both mobile and desktop)
  const desktopEdgePadPx = 180; // Desktop: 180px from edges (adjustable)
  const mobileEdgePadPx = 22;   // Mobile: 20px from edges (adjustable)

  const edgePadVW = isMobile
    ? (mobileEdgePadPx / vw) * 100           // Mobile: fixed 20px
    : (desktopEdgePadPx / vw) * 100;         // Desktop: fixed 180px

  // Final gap between badges when merged
  // Both desktop AND mobile use fixed pixel targets for consistency across ALL resolutions
  const desktopGapPx = 480; // Target gap in pixels for desktop (adjustable)
  const mobileGapPx = 330;  // Target gap in pixels for mobile (adjustable)

  const finalGapVW = isMobile
    ? (mobileGapPx / vw) * 100       // Mobile: fixed 280px target
    : (desktopGapPx / vw) * 100;     // Desktop: fixed 480px target

  // VISUAL PADDING CONTROLS (for badge positioning inside pill - in vw, scales dynamically)
  // Separate controls for mobile and desktop

  // Initial state padding (when badges are at edges)
  const pillPaddingLeftStart = isMobile ? 2 : 1;   // Mobile: 2vw, Desktop: 2vw left spacing at start
  const pillPaddingRightStart = isMobile ? 5 : 2;  // Mobile: 5vw, Desktop: 5vw right spacing at start

  // Final state padding (when badges are merged)
  const pillPaddingLeftEnd = isMobile ? 2 : 0.5;     // Mobile: 2vw, Desktop: 2vw left spacing when merged
  const pillPaddingRightEnd = isMobile ? 5 : 1.5;    // Mobile: 5vw, Desktop: 5vw right spacing when merged

  // Calculate positions in viewport width units WITH padding applied
  const leftStartVW = edgePadVW + pillPaddingLeftStart;
  const rightStartVW = 100 - edgePadVW - badgeSizeVW - pillPaddingRightStart;

  // Final positions when badges are near center WITH padding applied
  const leftEndVW = 50 - (finalGapVW / 2) - (badgeSizeVW / 2) + pillPaddingLeftEnd;
  const rightEndVW = 50 + (finalGapVW / 2) - (badgeSizeVW / 2) - pillPaddingRightEnd;

  // Calculate movement deltas in vw units
  const leftDeltaVW = leftEndVW - leftStartVW;
  const rightDeltaVW = rightEndVW - rightStartVW;

  // Badge movement animations - Using viewport units for smooth responsive animation
  const leftXVW = useTransform(progress,
    [0, 0.3, 0.7, 1],
    [`0vw`, `${leftDeltaVW * 0.3}vw`, `${leftDeltaVW * 0.85}vw`, `${leftDeltaVW}vw`]
  );
  const rightXVW = useTransform(progress,
    [0, 0.3, 0.7, 1],
    [`0vw`, `${rightDeltaVW * 0.3}vw`, `${rightDeltaVW * 0.85}vw`, `${rightDeltaVW}vw`]
  );

  // PILL ANIMATIONS
  // Pill is always visible
  const pillOpacity = useTransform(progress, [0, 0.2, 0.6, 1], [1, 1, 1, 1]);

  // PILL BACKGROUND ANIMATION
  // Pill width is calculated from BADGE POSITIONS ONLY (no padding in calculation)
  // Padding is for VISUAL SPACING inside the pill (doesn't affect pill width)

  // PILL WIDTH = BADGE DISTANCE ONLY (change badge positions to affect width)
  // Note: We calculate from base positions (without padding) to keep width independent
  const initialPillWidthVW = (100 - 2 * edgePadVW - badgeSizeVW) + badgeSizeVW;
  const finalPillWidthVW = finalGapVW + badgeSizeVW;

  const pillBgWidth = useTransform(progress,
    [0, 0.3, 0.7, 1],
    isMobile
      ? [`${initialPillWidthVW}vw`, `${initialPillWidthVW * 0.97}vw`, `${initialPillWidthVW * 0.95}vw`, `${finalPillWidthVW}vw`]
      : [`${initialPillWidthVW}vw`, `${initialPillWidthVW * 0.8}vw`, `${initialPillWidthVW * 0.55}vw`, `${finalPillWidthVW}vw`]
  );

  // No horizontal scaling needed - width animation handles it
  const pillBgScaleY = useTransform(progress, [0, 1], [0.96, 1]);

  // Fixed nav container dimensions to prevent shifting
  // Nav container stays constant to prevent content reflow
  const navContainerWidth = isMobile ? "280px" : "320px";

  // INTERNAL BADGE ANIMATIONS - REMOVED (using single badges now)

  // Active tab control (uncontrolled fallback)
  // ============================================================================
  // EXTERNAL BADGE ANIMATIONS (far left/right badges)
  // ============================================================================
  // Badge opacity: keep visible throughout (no fade out)
  const externalBadgeOpacity = useTransform(progress, [0, 1], [1, 1]);
  // Badge scale: gradual scale down from full size to mini size
  const externalBadgeScale = useTransform(progress, [0, 0.5, 0.8, 1], [1, 0.98, 0.96, 0.95]); // More gradual scaling

  // Keep pill background width in a motion value for smooth animation
  const [internalActive, setInternalActive] = React.useState<string>(navItems[0]?.label ?? "Overview");
  const currentActive = activeTab ?? internalActive;

  // Profile menu state
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Handle click outside user menu
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleTabClick(e: React.MouseEvent<HTMLAnchorElement>, item: NavItem) {
    onTabChange?.(item.label);
    if (!activeTab) setInternalActive(item.label);
    // If no href or href==="#", stop the jump
    if (!item.href || item.href === "#") {
      e.preventDefault();
    }
  }

  return (
    <div className={cx("fixed top-0 left-0 right-0 z-50", className)}>
      {/* Fixed header backdrop with animated background */}
      <motion.div
        style={{ height: headerHeight }}
        className="relative flex items-center select-none"
      >
        {/* Animated background - matches page gradient */}
        <motion.div
          className="absolute inset-0 -z-10 bg-gradient-to-br from-gray-950 via-gray-900 to-black"
          style={{ opacity: headerBgOpacity }}
        />
        {/* Animated border - fades in on scroll */}
        <motion.div
          className="absolute inset-x-0 bottom-0 h-px bg-white/10"
          style={{ opacity: headerBorderOpacity }}
        />

        {/* External Far-Left Badge (Brand) */}
        <motion.div
          className="absolute z-[103] h-10 w-10"
          style={{
            left: `${leftStartVW}vw`,
            x: leftXVW,
            opacity: externalBadgeOpacity,
            scale: externalBadgeScale
          }}
        >
          <div
            className="inline-flex items-center justify-center h-10 w-10"
            title="Brand"
          >
            {logoSrcLeft ? (
              <img
                src={logoSrcLeft}
                alt="Brand"
                className="h-6 w-6 object-contain"
                draggable={false}
              />
            ) : (
              <span className="font-semibold text-white text-lg">L</span>
            )}
          </div>
        </motion.div>

        {/* External Far-Right Badge (Profile) - Functional */}
        <motion.div
          className="absolute z-[103] h-10 w-10"
          style={{
            left: `${rightStartVW}vw`,
            x: rightXVW,
            opacity: externalBadgeOpacity,
            scale: externalBadgeScale
          }}
        >
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-white/5 transition-all duration-200"
            title="User menu"
          >
            <User className="w-5 h-5 text-white" />
          </button>
        </motion.div>

        {/* Center merged pill with animated background */}
        <div className="w-full flex items-center justify-center pointer-events-none">
          <div className="relative pointer-events-auto">
            {/* Animated background that morphs from full width to centered */}
            <motion.div
              className="absolute h-14 rounded-full bg-gradient-to-br from-gray-950 via-gray-900 to-black ring-1 ring-white/10 shadow-2xl"
              style={{
                width: pillBgWidth,
                scaleY: pillBgScaleY,
                opacity: pillOpacity,
                left: "50%",
                x: "-50%",
                top: "50%",
                y: "-50%"
              }}
            />

            {/* Fixed nav container - never changes size to prevent shifting */}
            <div
              className="relative flex items-center justify-center h-14 px-4"
              style={{ width: navContainerWidth }}
            >
              {/* NAV - Always visible - Fixed width container */}
              <nav aria-label="Primary" className="flex items-center justify-center">
                <ul className="flex items-center justify-center gap-1">
                  {navItems.map((item) => {
                    const isActive = item.label === currentActive;
                    return (
                      <li key={item.label}>
                        <a
                          href={item.href ?? "#"}
                          onClick={(e) => handleTabClick(e, item)}
                          className={cx(
                            "py-2 rounded-full font-medium transition text-white",
                            // Fixed text sizing to prevent layout shifts
                            "text-sm px-3",
                            "hover:bg-white/10",
                            isActive && "bg-white/10"
                          )}
                        >
                          {item.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </motion.div>

      {/* User Menu Dropdown */}
      <AnimatePresence>
        {showUserMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, type: "spring", bounce: 0.3 }}
            className="fixed w-64 z-[102]"
            style={{
              top: `calc(${headerHeightEnd} + 1rem)`,
              left: `${rightStartVW + badgeSizeVW}vw`,
              x: rightXVW,
              translateX: '-100%'
            }}
            ref={menuRef}
          >
            {/* Glass morphism dropdown */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              {/* Background blur */}
              <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-2xl" />
              <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-white/0" />

              {/* Content */}
              <div className="relative">
                {/* User info */}
                <div className="px-4 py-4 border-b border-gray-800/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                      <User size={20} className="text-gray-900" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {profile?.name || 'User'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Menu items */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      setActiveTab?.('profile');
                      setShowUserMenu(false);
                    }}
                    className="w-full px-4 py-3 flex items-center gap-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
                  >
                    <Settings size={18} />
                    <span>Settings</span>
                  </button>

                  {isDevelopmentMode && handleResetAllApplications && (
                    <button
                      onClick={() => {
                        handleResetAllApplications();
                        setShowUserMenu(false);
                      }}
                      disabled={loading}
                      className="w-full px-4 py-3 flex items-center gap-3 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-200"
                    >
                      <FlaskConical size={18} />
                      <span>Reset Applications</span>
                    </button>
                  )}

                  <div className="my-2 border-t border-gray-800/50" />

                  <button
                    onClick={() => {
                      logout?.();
                      setShowUserMenu(false);
                    }}
                    className="w-full px-4 py-3 flex items-center gap-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
                  >
                    <LogOut size={18} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- External Badge Component (edge → center travel) ---------- */

function ExternalBadge({
  left,
  x,
  opacity,
  scale,
  imgSrc,
  alt,
  fallbackInitial = "•",
}: {
  left: number; // absolute LEFT px at start
  x: MotionValue<number>;
  opacity: MotionValue<number>;
  scale: MotionValue<number>;
  imgSrc?: string;
  alt: string;
  fallbackInitial?: string;
}) {
  return (
    <motion.div
      className="absolute"
      style={{ left, x, opacity, scale }}
      // Keep above pill until it fades
    >
      <Circle title={alt}>
        {imgSrc ? (
          // Avoid Next/Image to keep this file framework-agnostic
          <img
            src={imgSrc}
            alt={alt}
            className="h-5 w-5 object-contain"
            draggable={false}
          />
        ) : (
          <span className="font-semibold">{fallbackInitial}</span>
        )}
      </Circle>
    </motion.div>
  );
}
